import React, { useState } from "react";
import { STATUS, statusLabel } from "./shared";

const WO_STATUS = [
  ["wo_quotation", "Work Order Quotation"],
  ["booked", "Work Order Booked"],
  ["completed", "Work Order Completed"],
];

// Build the pink-section text for each quick-fill button from the client's data.
export function regularService(client, contract) {
  const lines = [];
  if (contract?.description) lines.push(contract.description);
  if (client.service_code) lines.push(`Service Code - ${client.service_code}`);
  if (client.regarding) lines.push(`Regarding: ${client.regarding}`);
  if (client.participant_number) lines.push(`Participant Number: ${client.participant_number}`);
  if (client.approval_number) lines.push(`Your Approval Number: ${client.approval_number}`);
  return lines.join("\n");
}
export function newRegularService(client, contract) {
  const lines = ["New Regular Service — Pool Service/Clean/Balance"];
  if (client.service_code) lines.push(`Service Code - ${client.service_code}`);
  if (client.regarding) lines.push(`Regarding: ${client.regarding}`);
  if (client.participant_number) lines.push(`Participant Number: ${client.participant_number}`);
  if (client.approval_number) lines.push(`Your Approval Number: ${client.approval_number}`);
  return lines.join("\n");
}
export function poolHandover(client) {
  const who = client.regarding || client.name || "";
  return [
    "Pool Handover",
    who ? `Regarding: ${who}` : "",
    "New pool handover — water balance, equipment demonstration, start-up chemicals and client instruction.",
  ].filter(Boolean).join("\n");
}

export default function WorkOrderSetupModal({ wo, client, contract, team = [], onDone, onClose }) {
  const [status, setStatus] = useState(statusLabel(wo.status) || "Work Order Quotation");
  const [issue, setIssue] = useState(wo.issue_text || "");
  const [tech, setTech] = useState(wo.technician || "");
  const [saving, setSaving] = useState(false);

  const setStatusVal = (v) => {
    const p = STATUS.find(([k, l]) => k === v || l.toLowerCase() === v.trim().toLowerCase());
    setStatus(p ? p[1] : v);
  };

  const done = async () => {
    if (saving) return;
    setSaving(true);
    const p = STATUS.find(([k, l]) => l.toLowerCase() === String(status).trim().toLowerCase());
    try {
      await onDone({ status: p ? p[0] : status, issue_text: issue, technician: tech });
    } finally { setSaving(false); }
  };

  const Btn = ({ onClick, children, cls, ...rest }) => (
    <button type="button" onClick={onClick} className={`rounded-md px-3 py-2 text-[12px] font-black shadow ${cls}`} {...rest}>{children}</button>
  );

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 overflow-auto bg-black/55 backdrop-blur-sm" data-testid="wo-setup-modal">
      <div className="w-full max-w-2xl my-8 rounded-2xl bg-white border border-[#5b8ac4] shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 bg-[#123a63] text-white rounded-t-2xl">
          <h2 className="font-display font-black text-lg">New Work Order{wo.wo_number ? ` #${wo.wo_number}` : ""} — Set Up</h2>
          <button onClick={onClose} data-testid="wo-setup-close" title="Close" className="text-white/80 hover:text-white text-xl leading-none">×</button>
        </div>

        <div className="p-5 space-y-4">
          {/* status */}
          <div>
            <label className="block text-[11px] font-black text-[#123a63] uppercase tracking-wide mb-1">Work Order Status — select or type anything</label>
            <input list="wo-setup-status-options" value={status} onChange={(e) => setStatusVal(e.target.value)}
              data-testid="wo-setup-status"
              className="w-full border-2 border-[#8a6d16] rounded px-3 py-2 text-[14px] font-bold text-[#123a63] bg-[#fffdf3] outline-none focus:ring-2 focus:ring-amber-300" />
            <datalist id="wo-setup-status-options">
              {WO_STATUS.map(([k, l]) => <option key={k} value={l} />)}
            </datalist>
          </div>

          {/* pink section + quick-fill buttons */}
          <div>
            <label className="block text-[11px] font-black text-[#123a63] uppercase tracking-wide mb-1">Work Order Detail (visible on Work Order, FM84 &amp; Client's Quote)</label>
            <div className="flex flex-wrap gap-2 mb-2">
              <Btn onClick={() => setIssue(regularService(client, contract))} cls="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="wo-setup-book-regular">BOOK REGULAR SERVICE</Btn>
              <Btn onClick={() => setIssue(newRegularService(client, contract))} cls="bg-sky-600 text-white hover:bg-sky-700" data-testid="wo-setup-new-regular">NEW REGULAR SERVICE</Btn>
              <Btn onClick={() => setIssue(poolHandover(client))} cls="bg-indigo-600 text-white hover:bg-indigo-700" data-testid="wo-setup-pool-handover">POOL HANDOVER</Btn>
              <Btn onClick={() => setIssue((v) => (v && v.includes("Maytronics E-Sale") ? v : `Maytronics E-Sale #${v ? "\n" + v : ""}`))} cls="bg-[#c8a12a] text-white hover:bg-[#b08f20]" data-testid="wo-setup-maytronics">Maytronics E-Sale #</Btn>
            </div>
            <textarea value={issue} onChange={(e) => setIssue(e.target.value)} rows={6} data-testid="wo-setup-issue"
              placeholder="Type the work order detail, or use a quick-fill button above…"
              className="w-full border border-[#d98fa0] bg-[#fff1f4] rounded px-3 py-2 text-[13px] text-slate-800 outline-none focus:ring-2 focus:ring-pink-300" />
          </div>

          {/* employee */}
          <div>
            <label className="block text-[11px] font-black text-[#123a63] uppercase tracking-wide mb-1">Allocate to Employee</label>
            <select value={tech} onChange={(e) => setTech(e.target.value)} data-testid="wo-setup-employee"
              className="w-full border border-[#8a6d16] rounded px-3 py-2 text-[14px] bg-white outline-none focus:ring-2 focus:ring-amber-300">
              <option value="">— choose employee —</option>
              {team.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button onClick={onClose} data-testid="wo-setup-cancel" className="rounded-md border border-slate-300 bg-white px-4 py-2 text-[13px] font-bold hover:bg-slate-50">Cancel</button>
          <button onClick={done} disabled={saving} data-testid="wo-setup-done" className="rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-black text-[14px] px-6 py-2 shadow">{saving ? "Saving…" : "DONE"}</button>
        </div>
      </div>
    </div>
  );
}
